import { useState } from 'react';
import { useUi } from '../i18n/uiTranslations';
import { useLanguage } from '../context/LanguageContext';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { SceneSection } from './Journey';
import { StaticChip } from './StaticVisuals';
import { TELEGRAM_URL, TELEGRAM_PATH } from './TelegramButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="field-label">{label}</span>
    {children}
  </label>
);

const SubmitButton = ({ loading, label, testId, sendingLabel }) => (
  <button type="submit" className="btn-primary w-full sm:w-auto group" disabled={loading} data-testid={testId}>
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
    {loading ? sendingLabel : label}
    {!loading && <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />}
  </button>
);

const tabCls = 'relative rounded-none bg-transparent px-0 pb-4 pt-0 font-display text-lg sm:text-2xl text-[#7C776E] data-[state=active]:bg-transparent data-[state=active]:text-[#F4F0E8] data-[state=active]:shadow-none after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:bg-[#C8AA73] after:origin-left after:scale-x-0 data-[state=active]:after:scale-x-100 after:transition-transform after:duration-500 hover:text-[#F4F0E8]/80 transition-colors';

export const ContactSection = ({ activeTab, setActiveTab }) => {
  const { t, language } = useLanguage();
  const u = useUi();
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', message: '' });
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '', phone: '', services: [], message: '' });

  const serviceOptions = ['aggregator', 'platform', 'payments', 'crypto'].map((id) => ({ id, label: t(`services.${id}.title`) }));

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...contactForm, language });
      toast.success(t('contact.success'));
      setContactForm({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      toast.error(u('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (demoForm.services.length === 0) {
      toast.error(u('selectService'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, { ...demoForm, language });
      toast.success(t('contact.demoSuccess'));
      setDemoForm({ name: '', email: '', company: '', phone: '', services: [], message: '' });
    } catch (error) {
      toast.error(u('error'));
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    setDemoForm((prev) => ({
      ...prev,
      services: prev.services.includes(serviceId) ? prev.services.filter((s) => s !== serviceId) : [...prev.services, serviceId],
    }));
  };

  const keepVisible = (e) => {
    if (window.innerWidth < 1024 && /INPUT|TEXTAREA/.test(e.target.tagName)) {
      const el = e.target;
      setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 350);
    }
  };

  const setC = (k) => (e) => setContactForm((p) => ({ ...p, [k]: e.target.value }));
  const setD = (k) => (e) => setDemoForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <SceneSection scene={6} chipControl sceneAt="top" id="contact" testId="contact-section" span={7} staticVisual={<StaticChip className="w-[min(80vw,calc(var(--band)*0.95))] lg:w-full lg:max-w-[400px] mx-auto" />}>
          <p className="eyebrow">{u('letsConnect')}</p>
          <h2 className="display-xl mt-8 text-[clamp(2.8rem,6vw,6rem)]" data-testid="contact-title">{t('contact.title')}</h2>
          <div className="mt-8 mb-12 flex flex-wrap items-center gap-x-8 gap-y-4">
            <a href="mailto:info@softmaya.com" className="link-lux inline-block font-display text-xl sm:text-2xl" data-testid="contact-email-link">info@softmaya.com</a>
            <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label={u('telegram')} className="link-lux inline-flex items-center gap-3 font-display text-xl sm:text-2xl" data-testid="contact-telegram-link">
              <svg className="w-5 h-5 text-[#C8AA73]" fill="currentColor" viewBox="0 0 24 24"><path d={TELEGRAM_PATH} /></svg>@softmayaofficial
            </a>
          </div>
          <div className="rounded-[24px] metal-edge bg-[#111110]/95 p-5 sm:p-10 lg:p-12" onFocusCapture={keepVisible}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="h-auto w-full justify-start flex-wrap gap-x-6 sm:gap-x-10 gap-y-2 bg-transparent p-0 border-b border-[#F4F0E8]/10 rounded-none mb-10">
                <TabsTrigger value="contact" className={tabCls} data-testid="contact-tab">{t('contact.contactTab')}</TabsTrigger>
                <TabsTrigger value="demo" className={tabCls} data-testid="demo-tab">{t('contact.demoTab')}</TabsTrigger>
              </TabsList>

              <TabsContent value="contact" className="mt-0">
                <form onSubmit={handleContactSubmit} className="space-y-6 rise-in">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label={t('contact.name')}>
                      <Input value={contactForm.name} onChange={setC('name')} className="input-dark" placeholder="John Doe" required data-testid="contact-name-input" />
                    </Field>
                    <Field label={t('contact.email')}>
                      <Input type="email" value={contactForm.email} onChange={setC('email')} className="input-dark" placeholder="john@company.com" required data-testid="contact-email-input" />
                    </Field>
                  </div>
                  <Field label={t('contact.company')}>
                    <Input value={contactForm.company} onChange={setC('company')} className="input-dark" placeholder={u('phCompany')} data-testid="contact-company-input" />
                  </Field>
                  <Field label={t('contact.message')}>
                    <Textarea value={contactForm.message} onChange={setC('message')} className="input-dark min-h-[150px] resize-none" placeholder={u('phMessage')} required data-testid="contact-message-input" />
                  </Field>
                  <div className="pt-2"><SubmitButton loading={loading} label={t('contact.submit')} testId="contact-form-submit" sendingLabel={u('sending')} /></div>
                </form>
              </TabsContent>

              <TabsContent value="demo" className="mt-0">
                <form onSubmit={handleDemoSubmit} className="space-y-6 rise-in">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label={t('contact.name')}>
                      <Input value={demoForm.name} onChange={setD('name')} className="input-dark" placeholder="John Doe" required data-testid="demo-name-input" />
                    </Field>
                    <Field label={t('contact.email')}>
                      <Input type="email" value={demoForm.email} onChange={setD('email')} className="input-dark" placeholder="john@company.com" required data-testid="demo-email-input" />
                    </Field>
                    <Field label={t('contact.company')}>
                      <Input value={demoForm.company} onChange={setD('company')} className="input-dark" placeholder={u('phCompany')} required data-testid="demo-company-input" />
                    </Field>
                    <Field label={t('contact.phone')}>
                      <Input type="tel" value={demoForm.phone} onChange={setD('phone')} className="input-dark" placeholder="+1 234 567 8900" data-testid="demo-phone-input" />
                    </Field>
                  </div>
                  <div>
                    <span className="field-label">{t('contact.services')}</span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {serviceOptions.map((s) => {
                        const on = demoForm.services.includes(s.id);
                        return (
                          <label
                            key={s.id}
                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl cursor-pointer border transition-colors duration-300 ${on ? 'border-[#C8AA73]/60 bg-[#C8AA73]/[0.06]' : 'border-[#F4F0E8]/10 hover:border-[#F4F0E8]/25'}`}
                            data-testid={`demo-service-${s.id}`}
                          >
                            <Checkbox
                              checked={on}
                              onCheckedChange={() => toggleService(s.id)}
                              className="border-[#F4F0E8]/30 data-[state=checked]:bg-[#C8AA73] data-[state=checked]:border-[#C8AA73] data-[state=checked]:text-[#080808]"
                            />
                            <span className={`text-sm ${on ? 'text-[#F4F0E8]' : 'text-[#ABA59A]'}`}>{s.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <Field label={t('contact.message')}>
                    <Textarea value={demoForm.message} onChange={setD('message')} className="input-dark min-h-[120px] resize-none" placeholder={u('phAdditional')} data-testid="demo-message-input" />
                  </Field>
                  <div className="pt-2"><SubmitButton loading={loading} label={t('contact.submitDemo')} testId="demo-form-submit" sendingLabel={u('sending')} /></div>
                </form>
              </TabsContent>
            </Tabs>
          </div>
    </SceneSection>
  );
};
